import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import TripPage from "./pages/TripPage";
import LoginPage from "./pages/LoginPage";
import TripSignUpPage from "./pages/TripSignUpPage";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import NotFound from "./pages/NotFound";
import HomePage from "./pages/HomePage";
import ImprintPage from "./pages/ImprintPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import BillingDemoPage from "./pages/BillingDemoPage";
import ErrorBoundary from "./components/layout/ErrorBoundary";
import Footer from "./components/layout/Footer";
import { PWAUpdateNotification } from "./components/ui/PWAUpdateNotification";
// MUI Theme Provider
import { ThemeProvider } from "./theme/ThemeProvider";
import { ToastProvider } from "./hooks/use-toast.tsx";
import { ConfirmProvider } from "./components/ui";
import { Box } from '@mui/material';

const queryClient = new QueryClient();

function AppContent() {
  const { currentUser } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/trip/:tripId/signup" element={<TripSignUpPage />} />
      <Route path="/imprint" element={<ImprintPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/billing-demo" element={<BillingDemoPage />} />

      {/* Protected Admin Routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/dashboard" element={<AdminDashboardPage />} />
        <Route path="/trip/:tripId" element={<TripPage />} />
        <Route path="/trip/:tripId/:tab" element={<TripPage />} />
      </Route>

      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => (
  <AuthProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <ConfirmProvider>
            <Router>
              <ErrorBoundary>
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                  <Box sx={{ flexGrow: 1, backgroundColor: 'grey.50' }}>
                    <AppContent />
                  </Box>
                  <Footer />
                </Box>
                <PWAUpdateNotification />
              </ErrorBoundary>
            </Router>
          </ConfirmProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </AuthProvider>
);

export default App;
